import { RootState } from '@/app/store.ts';
import { AccountType } from '@/features/accounts/types.ts';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AccountsState {
  displayMode: 'grid' | 'cards';
  dialogInviteOpen: boolean;
  invitationToCancel?: AccountType;
  inviteEmail: string | undefined;
  inviteContactName?: string;
  inviteCompanyName?: string;
  dialogCancelInviteOpen: boolean;
  inputErrors: Partial<Record<'inviteEmail', string>>;
}

const initialState: AccountsState = {
  displayMode: 'cards',
  dialogInviteOpen: false,
  dialogCancelInviteOpen: false,
  invitationToCancel: undefined,
  inviteEmail: undefined,
  inviteContactName: undefined,
  inviteCompanyName: undefined,

  inputErrors: {},
};

export const AccountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    setDisplayMode: (state, action: PayloadAction<'grid' | 'cards'>) => {
      state.displayMode = action.payload;
    },

    setDialogInviteOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogInviteOpen = action.payload;
      state.inviteEmail = '';
      state.inviteContactName = '';
      state.inviteCompanyName = '';
    },

    openDialogCancelInvite: (state, action: PayloadAction<AccountType>) => {
      if (state.dialogCancelInviteOpen) {
        throw new Error('Dialog is already open');
      }

      const account = action.payload;

      if (!account.id) {
        throw new Error('Account ID is required');
      }

      if (!account.isInvitation) {
        throw new Error('Account is not an invitation');
      }

      state.dialogCancelInviteOpen = true;
      state.invitationToCancel = account;
    },

    closeDialogCancelInvite: (state) => {
      if (!state.dialogCancelInviteOpen) {
        throw new Error('Dialog is not open');
      }

      state.dialogCancelInviteOpen = false;
      state.invitationToCancel = undefined;
    },

    setInviteEmail: (state, action: PayloadAction<string>) => {
      state.inviteEmail = action.payload;
    },

    setInviteContactName: (state, action: PayloadAction<string>) => {
      state.inviteContactName = action.payload;
    },

    setInviteCompanyName: (state, action: PayloadAction<string>) => {
      state.inviteCompanyName = action.payload;
    },

    setInputErrors: (
      state,
      action: PayloadAction<{
        input: keyof AccountsState['inputErrors'];
        error: string;
      }>,
    ) => {
      state.inputErrors = {
        ...state.inputErrors,
        [action.payload.input]: action.payload.error,
      };
    },
  },
});

export const {
  setDisplayMode,
  setDialogInviteOpen,
  openDialogCancelInvite,
  closeDialogCancelInvite,
  setInviteEmail,
  setInviteContactName,
  setInviteCompanyName,
  setInputErrors,
} = AccountsSlice.actions;

/**
 * Slice for customer account
 *
 * @param state the root state
 * @returns     the state
 */
export const getAccountsState = (state: RootState) => state.accounts;
